<template>
  <v-container fluid>
    <PageHeader title="Data Quality">
      <template #icon>
        <v-icon
          class="mr-4"
          size="36"
        >
          mdi-clipboard-check-outline
        </v-icon>
      </template>
      <template #actions>
        <v-btn
          class="mr-2"
          variant="tonal"
          prepend-icon="mdi-history"
          @click="auditDialog = true"
        >
          Audit Log
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          prepend-icon="mdi-refresh"
          @click="refresh"
        >
          Re-run checks
        </v-btn>
      </template>
    </PageHeader>

    <div
      v-if="loading && !report"
      class="d-flex justify-center py-12"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <template v-else-if="report">
      <div class="d-flex align-center flex-wrap mt-4 mb-6 summary-row">
        <v-chip
          class="mr-3 mb-2"
          :color="report.totalIssues === 0 ? 'success' : 'default'"
          variant="flat"
        >
          {{ report.totalIssues }} total issue{{ report.totalIssues === 1 ? "" : "s" }}
        </v-chip>
        <v-chip
          v-if="report.errorCount > 0"
          class="mr-3 mb-2"
          color="error"
          variant="tonal"
        >
          {{ report.errorCount }} error{{ report.errorCount === 1 ? "" : "s" }}
        </v-chip>
        <v-chip
          v-if="report.warningCount > 0"
          class="mr-3 mb-2"
          color="warning"
          variant="tonal"
        >
          {{ report.warningCount }} warning{{ report.warningCount === 1 ? "" : "s" }}
        </v-chip>
        <span class="text-caption text-medium-emphasis mb-2">
          Last run {{ generatedAtDisplay }}
        </span>
      </div>

      <v-alert
        v-if="report.totalIssues === 0"
        type="success"
        variant="tonal"
        class="mb-6"
      >
        All checks passed — no data-quality issues found.
      </v-alert>

      <div
        v-for="category in orderedCategories"
        :key="category"
        class="mb-6"
      >
        <h3 class="text-subtitle-1 font-weight-medium mb-2">
          {{ category }}
        </h3>
        <v-expansion-panels
          multiple
          variant="accordion"
        >
          <v-expansion-panel
            v-for="check in checksByCategory[category]"
            :key="check.key"
            :disabled="check.count === 0"
          >
            <v-expansion-panel-title>
              <div class="d-flex align-center check-title">
                <v-icon
                  :color="check.count === 0 ? 'success' : severityColor(check.severity)"
                  class="mr-3"
                >
                  {{ check.count === 0 ? "mdi-check-circle" : severityIcon(check.severity) }}
                </v-icon>
                <span class="check-name">{{ check.title }}</span>
                <v-chip
                  v-if="check.count > 0"
                  size="small"
                  class="ml-3"
                  :color="severityColor(check.severity)"
                  variant="tonal"
                >
                  {{ check.count }}
                </v-chip>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-body-2 text-medium-emphasis mb-3">
                {{ check.description }}
              </p>
              <v-list
                density="compact"
                class="issue-list"
              >
                <v-list-item
                  v-for="(issue, index) in check.issues"
                  :key="index"
                  :class="{ 'issue-openable': isOpenable(issue) }"
                  @click="openIssue(issue)"
                >
                  <template #prepend>
                    <v-icon
                      size="small"
                      class="mr-2"
                    >
                      {{ entityIcon(issue.entityType) }}
                    </v-icon>
                  </template>
                  <v-list-item-title>{{ issue.label }}</v-list-item-title>
                  <v-list-item-subtitle>{{ issue.detail }}</v-list-item-subtitle>
                  <template #append>
                    <v-btn
                      v-if="check.key === 'duplicate_whale_id'"
                      size="small"
                      variant="tonal"
                      color="primary"
                      prepend-icon="mdi-call-merge"
                      @click.stop="openMerge(issue)"
                    >
                      Merge
                    </v-btn>
                    <v-icon
                      v-else-if="isOpenable(issue)"
                      size="small"
                    >
                      mdi-open-in-new
                    </v-icon>
                  </template>
                </v-list-item>
              </v-list>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </template>

    <v-alert
      v-else
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Could not load the data-quality report.
    </v-alert>

    <whale-rich-view />
    <audit-log-dialog v-model="auditDialog" />
    <merge-dialog
      v-model="mergeDialog"
      :whale-id="mergeWhaleId"
      @merged="onMerged"
    />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import PageHeader from "@/components/PageHeader";
import WhaleRichView from "@/components/whale/WhaleRichView.vue";
import AuditLogDialog from "@/components/audit/AuditLogDialog.vue";
import MergeDialog from "@/components/whale/MergeDialog.vue";

const CATEGORY_ORDER = ["Lineage", "Catalogue", "Sightings", "Photos"];

export default {
  name: "QualityPage",
  components: {
    PageHeader,
    WhaleRichView,
    AuditLogDialog,
    MergeDialog,
  },
  data() {
    return {
      auditDialog: false,
      mergeDialog: false,
      mergeWhaleId: null,
    };
  },
  computed: {
    ...mapGetters({
      report: "quality/report",
      loading: "quality/loading",
    }),
    checksByCategory() {
      const grouped = {};
      if (this.report && this.report.checks) {
        this.report.checks.forEach((check) => {
          (grouped[check.category] = grouped[check.category] || []).push(check);
        });
      }
      return grouped;
    },
    orderedCategories() {
      const present = Object.keys(this.checksByCategory);
      const ordered = CATEGORY_ORDER.filter((category) => present.includes(category));
      const extras = present.filter((category) => !CATEGORY_ORDER.includes(category));
      return ordered.concat(extras);
    },
    generatedAtDisplay() {
      if (!this.report || !this.report.generatedAt) {
        return "";
      }
      const date = new Date(this.report.generatedAt);
      return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    ...mapActions({
      loadReport: "quality/loadReport",
      openByWhaleId: "whale/view/openByWhaleId",
    }),
    refresh() {
      this.loadReport();
    },
    severityColor(severity) {
      return severity === "error" ? "error" : "warning";
    },
    severityIcon(severity) {
      return severity === "error" ? "mdi-alert-circle" : "mdi-alert";
    },
    entityIcon(entityType) {
      if (entityType === "sighting") {
        return "mdi-calendar-check";
      }
      if (entityType === "image") {
        return "mdi-camera";
      }
      return "mdi-fish";
    },
    isOpenable(issue) {
      return issue.entityType === "whale" && !!issue.whaleId && issue.entityId != null;
    },
    openIssue(issue) {
      if (this.isOpenable(issue)) {
        this.openByWhaleId({ whaleId: issue.whaleId, mode: "rich" });
      }
    },
    openMerge(issue) {
      this.mergeWhaleId = issue.whaleId || issue.label;
      this.mergeDialog = true;
    },
    onMerged() {
      this.mergeDialog = false;
      this.loadReport();
    },
  },
};
</script>

<style scoped>
.check-name {
  font-weight: 500;
}

.issue-openable {
  cursor: pointer;
}

.summary-row {
  row-gap: 4px;
}
</style>
